import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Subject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  MatSnackBar,
  MatSnackBarVerticalPosition,
  MatSnackBarHorizontalPosition,
} from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialog } from '@app/shared/confirm-dialog/confirm-dialog';
import { LoadingDialog } from '@app/shared/loading-dialog/loading-dialog';
import { InfoDialog} from '@app/shared/info-dialog/info-dialog';
import { formatDate } from '@angular/common';
import { AuthService } from '@app/auth/auth.service';
import { Router } from '@angular/router';
import { ApiResponse } from '@app/models/api-response.model';
import { Kpi } from '@app/models/kpi.model';
import { Setting } from '@app/models/setting.model';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  private baseUrl: string = environment.apiBaseUrl + 'admin/';

  private headerRefresh = new Subject<void>();
  headerRefresh$ = this.headerRefresh.asObservable();

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
  ) {}

  isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  triggerHeaderRefresh(): void {
    this.headerRefresh.next();
  }

  // General CRUD Operations
  get(endpoint: string, data: any = {}): Observable<any> {
    const queryString = new URLSearchParams(data).toString();
    return this.http.get(`${this.baseUrl}${endpoint}?${queryString}`);
  }

  post(endpoint: string, data: any = {}): Observable<any> {
    return this.http.post(`${this.baseUrl}${endpoint}`, data);
  }

  index(endpoint: string): Observable<any> {
    return this.http.get(`${this.baseUrl}${endpoint}`);
  }

  show(endpoint: string, id: number): Observable<any> {
    return this.http.get(`${this.baseUrl}${endpoint}/${id}`);
  }

  put(endpoint: string, id: number, data: any): Observable<any> {
    return this.http.put(`${this.baseUrl}${endpoint}/${id}`, data);
  }

  patch(endpoint: string, id: number, data: any): Observable<any> {
    return this.http.patch(`${this.baseUrl}${endpoint}/${id}`, data);
  }

  delete(endpoint: string, id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}${endpoint}/${id}`);
  }

  search(endpoint: string, data: any): Observable<any> {
    // const queryString = new URLSearchParams(data).toString();
    const queryString = this.toHttpParams(data).toString();
    return this.http.get(`${this.baseUrl}${endpoint}?${queryString}`);
  }

  // Snackbar Notification
  showSnackBar(
    message: string,
    type: string = 'info',
    duration: number = 3,
    position: MatSnackBarVerticalPosition = 'top',
  ) {
    const dismiss = 'Close';
    const classs = `${type}-snackbar`;

    this.snackBar.open(message, dismiss, {
      duration: duration * 1000,
      horizontalPosition: 'center' as MatSnackBarHorizontalPosition,
      verticalPosition: position,
      panelClass: [classs],
    });
  }

  successSnackBar(message: string) {
    this.showSnackBar(message, 'success');
  }

  warningSnackBar(message: string) {
    this.showSnackBar(message, 'warning');
  }

  errorSnackBar(message: string) {
    this.showSnackBar(message, 'error');
  }

  // Confirmation Dialog
  confirm(question: string): Observable<string> {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      // width: '300px',
      data: { question },
    });

    return dialogRef.afterClosed(); // Returns true or false
  }

  openInfoDialog(message: string) {
    this.dialog.open(InfoDialog, {
      data: { message: message },
    });
  }

  openLoadingDialog(message: string = 'Please wait...') {
    this.dialog.open(LoadingDialog, {
      disableClose: true,
      data: { message: message },
    });
  }

  closeLoadingDialog() {
    this.dialog.closeAll();
  }

  handleError(error: any) {
    const errorMessage = error.error.message ?? 'Unknown error';

    if (errorMessage !== 'Unauthenticated.') {
      this.showSnackBar(errorMessage, 'error');
    }
  }

  getDashboardKpi(): Observable<ApiResponse<Kpi>> {
    return this.http.get<ApiResponse<Kpi>>(
      `${this.baseUrl}stats/kpi`
    );
  }

  getUsers(): Observable<any> {
    return this.http.get(`${this.baseUrl}users`);
  }

  getTickets(): Observable<any> {
    return this.http.get(`${this.baseUrl}tickets`);
  }

  resolveTicket(ticketId: number, data: any): Observable<any> {
    return this.http.patch(`${this.baseUrl}tickets/${ticketId}/resolve`, data);
  }

  getSettings(): Observable<ApiResponse<Setting>> {
    return this.http.get<ApiResponse<Setting>>(
      `${this.baseUrl}settings`
    );
  }

  updateSettings(data: Partial<Setting>): Observable<ApiResponse<Setting>> {
    return this.http.patch<ApiResponse<Setting>>(
      `${this.baseUrl}settings`,
      data
    );
  }

  private toHttpParams(data: any): HttpParams {
    let params = new HttpParams();

    Object.entries(data || {}).forEach(([key, raw]) => {
      let val: any = raw;

      // drop null/undefined/'' and the literal strings "null"/"undefined"
      if (
        val === null ||
        val === undefined ||
        val === '' ||
        val === 'null' ||
        val === 'undefined'
      )
        return;

      // normalize Date -> YYYY-MM-DD
      if (val instanceof Date) val = formatDate(val, 'yyyy-MM-dd', 'en-US');

      if (Array.isArray(val)) {
        val
          .filter((v) => v !== null && v !== undefined && v !== '')
          .forEach((v) => (params = params.append(`${key}[]`, String(v))));
      } else if (typeof val === 'object') {
        // for nested objects either stringify or flatten
        params = params.set(key, JSON.stringify(val));
      } else {
        params = params.set(key, String(val));
      }
    });

    return params;
  }
}
